import { lazy, Suspense } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { ProductCard } from "@/components/ProductCard";
import { HeroCarousel } from "@/components/HeroCarousel";
import { AnnouncementBar } from "@/components/AnnouncementBar";

import { transformImage } from "@/lib/product-images";
import heroImg from "@/assets/hero.jpg";

const SocialReel = lazy(() => import("@/components/SocialReel").then(m => ({ default: m.SocialReel })));
const SiteFooter = lazy(() => import("@/components/SiteFooter").then(m => ({ default: m.SiteFooter })));
const WhatsAppFab = lazy(() => import("@/components/WhatsAppFab").then(m => ({ default: m.WhatsAppFab })));
const CertificatePopup = lazy(() => import("@/components/CertificatePopup").then(m => ({ default: m.CertificatePopup })));


const productsQuery = queryOptions({
  queryKey: ["products", "all"],
  queryFn: async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    } catch (e) {
      // Keep the page rendering if the database is briefly unreachable.
      console.error("Failed to load products:", e);
      return [];
    }
  },
  staleTime: 30_000,
});

const heroSlidesQuery = queryOptions({
  queryKey: ["hero-slides"],
  queryFn: async () => {
    try {
      const { data, error } = await supabase
        .from("hero_slides")
        .select("*")
        .eq("active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    } catch (e) {
      console.error("Failed to load hero slides:", e);
      return [];
    }
  },
  staleTime: 60_000,
});

export const Route = createFileRoute("/")({
  head: ({ loaderData }) => {
    const lcp = (loaderData as { lcpImage?: string } | undefined)?.lcpImage;
    return {
      meta: [
        { title: "Jul's Skin — Premium Skincare & Hair Care" },
        { name: "description", content: "Shop curated skincare and hair care from Jul's Skin — gentle, effective formulas for everyday glow and healthy hair." },
        { property: "og:title", content: "Jul's Skin — Premium Skincare & Hair Care" },
        { property: "og:description", content: "Shop curated skincare and hair care from Jul's Skin — gentle, effective formulas for everyday glow and healthy hair." },
        { property: "og:url", content: "https://julsskin.shop/" },
        { name: "twitter:title", content: "Jul's Skin — Premium Skincare & Hair Care" },
        { name: "twitter:description", content: "Shop curated skincare and hair care from Jul's Skin — gentle, effective formulas for everyday glow and healthy hair." },
      ],
      links: [
        { rel: "canonical", href: "https://julsskin.shop/" },
        ...(lcp ? [{ rel: "preload", as: "image", href: lcp, fetchpriority: "high" } as const] : []),
      ],
    };
  },
  loader: async ({ context }) => {
    const [, slides] = await Promise.all([
      context.queryClient.ensureQueryData(productsQuery),
      context.queryClient.ensureQueryData(heroSlidesQuery),
    ]);
    const firstUrl = slides[0]?.image_url ?? heroImg;
    return { lcpImage: transformImage(firstUrl, 1600, 70) };
  },
  component: Home,
});

function Home() {
  const { data: products } = useSuspenseQuery(productsQuery);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <AnnouncementBar />
      <main id="main">
        <h1 className="sr-only">Jul's Skin & Hair Care — Natural Solutions for Skin and Hair</h1>
        <HeroCarousel />

        {/* Meesho-style category circles (hidden) */}
        {/* <CategoryStrip /> */}

        <div className="hidden">
          {`'''Do not make any visual modifications. The phrases I write are commands to understand what I want, not to be written down. Understand their content well, then execute what is required.'''
                                        
                                            
                                            hide`}
        </div>





        {/* All products */}
        <section className="mx-auto max-w-7xl w-full px-3 sm:px-4 pt-6 pb-10">
          <div className="bg-card rounded-md p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-bold">Best Sale</h2>
              <Link to="/shop" className="text-xs font-semibold text-primary">View All ›</Link>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">No products yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                {products.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} />)}
              </div>
            )}
          </div>
        </section>

        <Suspense fallback={null}><SocialReel /></Suspense>
      </main>
      <Suspense fallback={null}><SiteFooter /></Suspense>
      <Suspense fallback={null}><WhatsAppFab /></Suspense>
      <Suspense fallback={null}><CertificatePopup /></Suspense>

    </div>
  );
}
